import alt from 'components/Dispatcher';
import LibraryActions from 'actions/library/LibraryActions';
import LibraryCreateActions from 'actions/library/LibraryCreateActions';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import LibraryHighlightSource from 'sources/library/LibraryHighlightSource';
import immutableStore from 'alt-utils/lib/ImmutableUtil';
import {Map, Set, List, Record} from 'immutable';
import ConstValues from 'utils/const';
import Utils from '../Utils';
import BrowserStore from 'stores/BrowserStore';
import {SearchResults} from 'records/Search';
import LibrarySource from 'sources/library/LibrarySource';
import sortBy from 'lodash/sortBy';

const DEFAULT_PAGE = 1;
const SEARCH_TYPE = {
  SEARCH: 'search',
  MOST_RECENTLY_ADDED: 'most_recently_added',
};

const FormState = Record({
  library: null,
  allCallers: Set(),
  callerSearchTerm: '',
  callersAutocomplete: [],
  callers: [],
  tag: null,
  tags: [],
  showCreateOrUpdateLibraryDialog: false,
  createOrUpdateLibraryDialogError: null,
  results: [],
  searchType: SEARCH_TYPE.SEARCH,
  tagToRemove: null,
});

// To support some legacy code that is storing things both
// as maps and lists
const MapListContainer = Record({
  map: null,
  list: null,
});

const Sources = Record({
  // all libraries user has access to
  libraries: null,
  // all callers user has access to
  callers: null,
  accountTags: null,
  // all tags user has access to
  tags: null,
  // all common tags user has access to
  commonTags: null,
  // all recent tags user has access to
  recentTags: null,
  teams: Map({}),
  departments: Map({}),
  users: Map({}),
  // current library that's opened by the user
  library: null,
  user: MapListContainer(),
  team: MapListContainer(),
  department: MapListContainer(),
});

const ShareAccessible = Record({
  // user-accessible departments used in the share-value dropdown
  departments: Map(),
  // user-accessible teams used in the share-value dropdown
  teams: Map(),
  team: MapListContainer(),
  department: MapListContainer(),
});

const AddToLibraryFormState = Record({
  currentLibrary: null,
  currentTags: [],
});

const State = Record({
  currentOpenLibraryDialog: null,
  libraryTagToDelete: null,
  shareAccessible: ShareAccessible(),
  sources: Sources(),
  formState: FormState(),
  addToLibraryFormState: AddToLibraryFormState(),
  searchResults: new SearchResults(),
  // `searchCalls` is true when the user is performing a search on /library
  searchCalls: false,
  // `true` if server has no elasticsearch index for the current user's account
  missingIndex: false,
  searchInfo: null,
  searchTotal: 0,
  searchType: null,
  adminManage: false,
  fileId: null,
  fileHighlights: List(),
  searchPage: DEFAULT_PAGE,
  searchLoading: false,
});

@immutableStore
class LibraryStore {
  constructor() {
    this.state = State();
    this.bindActions(LibraryActions);
    this.bindActions(LibraryCreateActions);
    this.bindActions(CrmSyncActions);
    this.registerAsync(LibrarySource);
    this.registerAsync(LibraryHighlightSource);
    this.utils = new Utils();
  }

  onUpdateLibraryTagToDelete(libraryTag) {
    const state = this.state.set('libraryTagToDelete', libraryTag);
    this.setState(state);
  }

  onUpdateLibrarySearchType(searchType) {
    const state = this.state.set('searchType', searchType);
    this.setState(state);
  }

  onResetSearchCriteria() {
    let state = this.state.setIn(['formState', 'library'], 'any');
    state = state.setIn(['formState', 'callers'], []);
    state = state.setIn(['formState', 'tags'], []);
    state = state.set('searchPage', DEFAULT_PAGE);
    this.setState(state);
  }

  onClearSearchInfo() {
    const state = this.state.set('searchInfo', null);
    this.setState(state);
  }

  onClearSearchResults() {
    let state = this.state.set('searchResults', new SearchResults());
    state = state.set('searchCalls', false);
    state = state.set('searchTotal', 0);
    state = state.set('searchPage', 1);
    state = state.set('fileId', null);
    this.setState(state);
  }

  onUpdateUserList(users) {
    let state = this.state.setIn(['sources', 'user', 'list'], users);
    this.setState(state);
  }

  onUpdateCurrentOpenDialog(openDialog) {
    this.setState(this.state.set('currentOpenLibraryDialog', openDialog));
  }

  hideCreateOrUpdateLibraryDialogPopup(adminManage) {
    let state = this.state.setIn(['formState', 'showCreateOrUpdateLibraryDialog'], false);
    state = state.setIn(['formState', 'createOrUpdateLibraryDialogError'], null);
    this.setState(state);
    this.refreshWholePage(adminManage);
  }

  showCreateUpdateLibraryDialog() {
    this.setState(this.state.setIn(['formState', 'showCreateOrUpdateLibraryDialog'], true));
    setImmediate(() => LibraryCreateActions.resetCreateForm());
  }

  refreshWholePage(adminManage) {
    setTimeout(() => {
      setImmediate(() => LibraryActions.getLibraries(adminManage));
      setImmediate(() => LibraryActions.getTags());
      setImmediate(() => LibraryActions.getRecentTags());
      setImmediate(() => LibraryActions.getCommonTags());
      setImmediate(() => LibraryActions.getShareItems());
      if (adminManage) {
        setImmediate(() => LibraryActions.getShareAccessibleDepartments());
        setImmediate(() => LibraryActions.getShareAccessibleTeams());
      }
    }, 300);
  }

  onShowCreateOrUpdateLibraryDialog() {
    this.showCreateUpdateLibraryDialog();
  }

  onHideCreateOrUpdateLibraryDialog(adminManage) {
    this.hideCreateOrUpdateLibraryDialogPopup(adminManage);
  }

  onCreateLibrarySuccess() {
    this.hideCreateOrUpdateLibraryDialogPopup();
  }

  onCreateLibraryError(resp) {
    this._updateCreateOrUpdateLibraryDialogError(resp);
  }

  _updateCreateOrUpdateLibraryDialogError(resp) {
    const data = resp.response.data;
    if (data.error && data.error.length > 0) {
      this.setState(
        this.state.setIn(['formState', 'createOrUpdateLibraryDialogError'], data.error)
      );
    }
  }

  onUpdateLibrary(args) {
    const data = args[0];
    const adminManage = args[1];
    const state = this.state.set('adminManage', adminManage || false);
    this.setState(state);

    setImmediate(() => this.getInstance().updateLibrary(data));
  }

  onUpdateLibrarySuccess() {
    this.hideCreateOrUpdateLibraryDialogPopup(this.state.get('adminManage'));

    const state = this.state.set('adminManage', false);
    this.setState(state);
  }

  onUpdateLibraryError(resp) {
    this._updateCreateOrUpdateLibraryDialogError(resp);
  }

  onDeleteLibraryTag(libraryTag) {
    setTimeout(() => {
      this.getInstance().deleteLibraryTag({
        file_upload_id: libraryTag.file_upload_id,
        delete_library_tags: [
          {
            library_id: libraryTag.library_id,
            tags: [libraryTag.tag_name],
          },
        ],
      });
    }, 0);
  }

  onDeleteLibraryTagSuccess(argsAndResponse) {
    const {args} = argsAndResponse;
    setImmediate(() => this.getInstance().getFileInfo(args[0]['file_upload_id']));
    const state = this.state.set('libraryTagToDelete', null);
    this.setState(state);
  }

  onDeleteLibrary(adminManage) {
    const state = this.state.set('adminManage', adminManage || false);
    this.setState(state);
  }

  onDeleteLibrarySuccess() {
    this.hideCreateOrUpdateLibraryDialogPopup(this.state.get('adminManage'));

    const state = this.state.set('adminManage', false);
    this.setState(state);
  }

  onGetLibraries(adminManage) {
    setImmediate(() => this.getInstance().getLibraries(adminManage));
  }

  onGetLibrariesSuccess(resp) {
    this.setState(
      this.state.setIn(
        ['sources', 'libraries'],
        sortBy(resp.data.libraries, l => l.name)
      )
    );
  }

  onAddFileToLibrary(data) {
    const {fileId, libraryId, tags, removeTags, changedHighlights} = data;
    setImmediate(() =>
      this.getInstance().addFileToLibrary(fileId, libraryId, tags, removeTags, changedHighlights)
    );
  }

  onAddFileToLibrarySuccess(argsAndResponse) {
    let state = this.state.setIn(['addToLibraryFormState', 'currentLibrary'], null);
    this.setState(state.setIn(['addToLibraryFormState', 'currentTags'], []));
    const {args} = argsAndResponse;
    setImmediate(() => this.getInstance().getFileInfo(args[0]));
    setImmediate(() => this.submitChangedHighlights(args[0], args[1], args[2], args[3], args[4]));
  }

  submitChangedHighlights(fileId, libraryId, added, removed, changedHighlights) {
    const _added = changedHighlights?.added || added;
    const _removed = changedHighlights?.removed || removed;
    _added?.forEach(highlightElasticId => {
      this.getInstance().addLibraryHighlight(libraryId, fileId, highlightElasticId);
    });
    _removed?.forEach(libraryHighlightId => {
      this.getInstance().removeLibraryHighlight(libraryId, fileId, libraryHighlightId);
    });
  }

  onFetchLibraryHighlights(data) {
    const libraryId = data[0];
    const fileId = data[1];
    setImmediate(() => this.getInstance().fetchLibraryHighlights(libraryId, fileId));
  }

  onFetchLibraryHighlightsSuccess(response) {
    this.setState(this.state.set('fileHighlights', List(response)));
  }

  onGetTagsSuccess(argsAndResponse) {
    const {args, response: resp} = argsAndResponse;
    const [libraries] = args;
    let state = this.state.setIn(['sources', 'tags'], resp.data.tags);
    if (!libraries || libraries.length === 0) {
      state = state.setIn(['sources', 'accountTags'], resp.data.tags);
    }
    this.setState(state);
  }

  onGetRecentTagsSuccess(resp) {
    this.setState(this.state.setIn(['sources', 'recentTags'], resp.data.tags));
  }

  onGetCommonTagsSuccess(resp) {
    this.setState(this.state.setIn(['sources', 'commonTags'], resp.data.tags));
  }

  onGetLibraryTag(args) {
    const [libraryId, tag] = args;
    setImmediate(() => this.getInstance().getLibraryTag(libraryId, tag));
  }

  onGetLibraryTagSuccess(resp) {
    const tagData = resp.data.tag;
    this.setState(this.state.setIn(['formState', 'tagToRemove'], tagData));
  }

  onGetShareItemsSuccess(resp) {
    let users = null;
    if (resp.data.users) {
      users = resp.data.users.map(user => {
        return Object.assign(user, {
          name: user.first_name + ' ' + user.last_name,
        });
      });
    }
    let teams = null;
    if (resp.data.teams) {
      teams = resp.data.teams;
    }
    let departments = null;
    if (resp.data.departments) {
      departments = resp.data.departments;
    }

    let state = this.state;
    if (users) {
      state = state.setIn(['sources', 'user', 'list'], users);
      state = state.setIn(['sources', 'user', 'map'], this.convert_list_to_map(users, 'user'));
    }
    if (teams) {
      state = state.setIn(['sources', 'team', 'list'], teams);
      state = state.setIn(['sources', 'team', 'map'], this.convert_list_to_map(teams, 'team'));
    }
    if (departments) {
      state = state.setIn(['sources', 'department', 'list'], departments);
      state = state.setIn(
        ['sources', 'department', 'map'],
        this.convert_list_to_map(departments, 'department')
      );
    }
    this.setState(state);
  }

  convert_list_to_map(data, type) {
    let res = Map();
    data.forEach(obj => {
      res = res.set(obj.id, Object.assign(obj, {type: type}));
    });
    return res;
  }

  onGetTags() {
    let libraries = null;
    let library = this.state.getIn(['formState', 'library']);
    if (library) {
      if (library.id) {
        library = library.id;
      }
      libraries = List([library]);
    }
    const users = List(this.state.getIn(['formState', 'callers'])).map(caller => caller.value);
    setImmediate(() => this.getInstance().getTags(libraries, users));
  }

  onGetRecentTags() {
    setImmediate(() => this.getInstance().getRecentTags());
  }

  onGetCommonTags() {
    setImmediate(() => this.getInstance().getCommonTags());
  }

  onCreateLibrary(data) {
    setImmediate(() => this.getInstance().createLibrary(data));
  }

  onGetShareItems(data) {
    setImmediate(() => this.getInstance().getShareItems());
  }

  onGetShareAccessibleDepartments() {
    setImmediate(() => this.getInstance().getShareAccessibleDepartments());
  }

  onGetShareAccessibleTeams() {
    setImmediate(() => this.getInstance().getShareAccessibleTeams());
  }

  onGetShareAccessibleDepartmentsSuccess(resp) {
    const deps = resp.data.departments;
    let state = this.state.setIn(['shareAccessible', 'department', 'list'], deps);
    state = state.setIn(
      ['shareAccessible', 'department', 'map'],
      this.convert_list_to_map(deps, 'department')
    );
    this.setState(state);
  }

  onGetShareAccessibleTeamsSuccess(resp) {
    const teams = resp.data.teams;
    let state = this.state.setIn(['shareAccessible', 'team', 'list'], teams);
    state = state.setIn(
      ['shareAccessible', 'team', 'map'],
      this.convert_list_to_map(teams, 'team')
    );
    this.setState(state);
  }

  onGetLibrarySuccess(argsAndResponse) {
    const {response: resp} = argsAndResponse;
    if (resp.data.library) {
      let state = this.state.setIn(['sources', 'library'], resp.data.library);
      if (
        this.state.get('currentOpenLibraryDialog') ===
        ConstValues.currentOpenLibraryDialog.addToLibrary
      ) {
        state = state.setIn(['addToLibraryFormState', 'currentTags'], resp.data.tags);
      }
      this.setState(state);
    }
    if (
      this.state.get('currentOpenLibraryDialog') !==
      ConstValues.currentOpenLibraryDialog.addToLibrary
    ) {
      this.setState(this.state.setIn(['formState', 'showCreateOrUpdateLibraryDialog'], true));
    }
  }

  onUpdateLibraryDropdown(libraryId) {
    this.setState(this.state.setIn(['formState', 'library'], libraryId));
    this.onGetTags();
  }

  onAddTag(tag) {
    const sourceTags = this.state.getIn(['sources', 'tags']);
    let tags = this.state.getIn(['formState', 'tags']);

    if (sourceTags.indexOf(tag) > -1 && tags.indexOf(tag) === -1) {
      tags.push(tag);
      this.setState(this.state.setIn(['formState', 'tags'], tags));
    }
  }

  onCreateTag(tag) {
    let tags = this.state.getIn(['sources', 'tags']);
    if (tags.indexOf(tags) > -1) {
      return;
    }
    tags.push(tag);
    this.setState(this.state.setIn(['sources', 'tags'], tags));
  }

  onRemoveTag(tag) {
    let tags = this.state.getIn(['formState', 'tags']);
    tags = this._tagsWithout(tags, tag);
    const state = this.state.setIn(['formState', 'tags'], tags);
    this.setState(state);
  }

  _tagsWithout(tags, tag) {
    const tagIndex = tags.indexOf(tag);
    if (tagIndex > -1) {
      tags.splice(tagIndex, 1);
    }
    return tags;
  }

  onGetCallersAutocomplete(args) {
    const [input, libraryId] = args;
    if (input && input.length > 0) {
      setImmediate(() => {
        this.getInstance().getCallersAutocomplete(input, libraryId);
      });
    }
  }

  onClearCallersAutocomplete() {
    const state = this.state.setIn(['formState', 'callersAutocomplete'], []);
    this.setState(state);
  }

  onGetCallersAutocompleteSuccess(resp) {
    const callers = resp.data.callers;
    let state = this.state.setIn(['formState', 'callersAutocomplete'], callers);
    let allCallers = state.getIn(['formState', 'allCallers']);
    allCallers = allCallers.union(Set(callers));
    state = state.setIn(['formState', 'allCallers'], allCallers);
    this.setState(state);
  }

  onUpdateCurrentCallers(callers) {
    let callerItems = List(callers);
    const allCallers = this.state.getIn(['formState', 'allCallers']);
    callerItems = callerItems
      .map(callerId => {
        if (typeof callerId === 'string') {
          return allCallers.find(item => item.value === callerId);
        }
        return callerId;
      })
      .toArray();
    this.setState(this.state.setIn(['formState', 'callers'], callerItems));
    this.onGetTags();
  }

  onUpdateSearchingCalls(searchCalls) {
    let state = this.state.set('searchCalls', searchCalls);
    if (!searchCalls) {
      state = state.set('searchPage', DEFAULT_PAGE);
      state = state.setIn(['formState', 'callers'], []);
      state = state.setIn(['formState', 'tags'], []);
    }
    this.setState(state);
  }

  onSearchCallsLoading() {
    let state = this.state.set('searchCalls', true);
    state = state.setIn(['formState', 'searchType'], SEARCH_TYPE.SEARCH);
    state = state.set('searchLoading', true);
    this.setState(state);
  }

  onGetInfoAboutFiles(fileIds) {
    this.getInstance().getInfoAboutFiles({file_upload_ids: fileIds});
  }

  onSearchCalls() {
    let library = 'any';
    if (this.state.getIn(['formState', 'library'])) {
      library = this.state.getIn(['formState', 'library']);
    }
    let callers = 'any';
    const formStateCallers = this.state.getIn(['formState', 'callers']);
    if (formStateCallers != null && formStateCallers.length > 0) {
      callers = this.state.getIn(['formState', 'callers']).map(caller => {
        // caller.value equals user.id
        return caller.value;
      });
    }
    const tags = this.state.getIn(['formState', 'tags']);
    const page = this.state.searchPage;
    const searchData = {
      library,
      callers,
      tags,
      page,
      timezone: BrowserStore.getState().timezone,
    };
    setImmediate(() =>
      this.getInstance()
        .search(searchData)
        .catch(error => (error.status === 404 ? null : Promise.resolve(error)))
    );
  }

  updateSearchCriteria(searchCriteria) {
    const {library, callers, tags, page} = searchCriteria;
    let state = this.state.setIn(['formState', 'library'], library);
    state = state.setIn(['formState', 'callers'], callers);
    state = state.setIn(['formState', 'tags'], tags);
    state = state.set('searchPage', page);
    this.setState(state);
  }

  onReinitLibrarySearchContext() {
    this.updateSearchCriteriaUsingSearchInfo();
  }

  updateSearchCriteriaUsingSearchInfo() {
    const searchInfo = this.state.get('searchInfo');
    if (searchInfo) {
      let {library, callers, tags} = this.state.get('searchInfo');
      if (!library) {
        library = 'any';
      } else if (library && library.id) {
        library = library.id;
      }
      let state = this.state.setIn(['formState', 'library'], library);
      state = state.setIn(['formState', 'callers'], callers);
      state = state.setIn(['formState', 'tags'], tags);
      state = state.set('searchPage', DEFAULT_PAGE);
      this.setState(state);
    }
  }

  _updateSearchResultsUsingArgsAndResponse(argsAndResponse) {
    const {response} = argsAndResponse;
    const state = this.updateSearchResults(response);
    const fileIds = state.searchResults.results.map(res => res.id);
    setImmediate(() => {
      this.getInstance().getInfoAboutFiles({file_upload_ids: fileIds});
    });
  }

  onSearchCallsSuccess(argsAndResponse) {
    this._updateSearchResultsUsingArgsAndResponse(argsAndResponse);
    if (this.state.get('missingIndex')) {
      const state = this.state.set('missingIndex', false);
      this.setState(state);
    }
  }

  onSearchCallsError(argsAndResponse) {
    const {response} = argsAndResponse;
    let missingIndex = response.status === 404;
    const state = this.state.set('missingIndex', missingIndex).set('searchLoading', false);
    this.setState(state);
  }

  onSearchMostRecentlyAddedLoading() {
    let state = this.state.set('searchCalls', true);
    state = state.setIn(['formState', 'searchType'], SEARCH_TYPE.MOST_RECENTLY_ADDED);
    this.setState(state);
  }

  onSearchMostRecentlyAdded() {
    const {searchPage} = this.state;
    setImmediate(() => this.getInstance().searchRecentlyAdded(searchPage));
  }

  onSearchMostRecentlyAddedSuccess(argsAndResponse) {
    this._updateSearchResultsUsingArgsAndResponse(argsAndResponse);
  }

  onSearchTag(tag) {
    this.onClearSearchResults();
    let state = this.state.set('searchCalls', true);
    this.setState(state);
    let searchCriteria = this._searchCriteria();
    searchCriteria.library = 'any';
    searchCriteria.callers = [];
    searchCriteria.tags = [tag];
    this.updateSearchCriteria(searchCriteria);
    this.onSearchCalls();
  }

  onSearchLibrary(library) {
    let searchCriteria = this._searchCriteria();
    searchCriteria.library = library;
    searchCriteria.callers = [];
    searchCriteria.tags = [];
    this.updateSearchCriteria(searchCriteria);
    this.onSearchCalls();
  }

  /** Update this.state.get('searchResults') using a standard search endpoint response */
  updateSearchResults(resp) {
    const results = resp.data.results;
    const searchResults = new SearchResults({
      fields: [],
      results: results,
    });
    let state = this.state.set('searchInfo', resp.data.search_info);
    state = state.set('searchResults', searchResults);
    state = state.set('searchTotal', resp.data.total);
    state = state.set('missingIndex', false);
    state = state.set('searchLoading', false);
    this.setState(state);
    return state;
  }

  _searchCriteria() {
    const state = this.state;
    return {
      library: state.getIn(['formState', 'library']),
      callers: state.getIn(['formState', 'callers']),
      tags: state.getIn(['formState', 'tags']),
      page: state.searchPage,
    };
  }

  onChangeLibraryPage(page) {
    let searchCriteria = this._searchCriteria();
    searchCriteria.page = page;
    this.updateSearchCriteria(searchCriteria);
    if (this.state.getIn(['formState', 'searchType']) === SEARCH_TYPE.SEARCH) {
      this.onSearchCalls();
    } else if (this.state.getIn(['formState', 'searchType']) === SEARCH_TYPE.MOST_RECENTLY_ADDED) {
      this.onSearchMostRecentlyAdded();
    }
  }

  onUpdateCurrentLibrary(library) {
    this.setState(this.state.setIn(['addToLibraryFormState', 'currentLibrary'], library));
    if (library && library.id) {
      setImmediate(() => this.getInstance().getLibrary(library.id, 'updateCurrentLibrary'));
    }
  }

  onUpdateCurrentTags(tags) {
    this.setState(this.state.setIn(['addToLibraryFormState', 'currentTags'], tags));
  }

  onFetchCustomContentSuccess(resp) {
    let searchResults = this.state.get('searchResults');
    searchResults = this.utils.searchResultsWithCustomContentData(searchResults, resp.data);
    const state = this.state.set('searchResults', searchResults);
    this.setState(state);
  }
}

export default alt.createStore(LibraryStore, 'LibraryStore');
